import React, { useState, useEffect } from 'react';
import DailyItineraryPlanner from './DailyItineraryPlanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TripPlanCard = ({ tripPlan, onModify, onBookActivity, sendMessage }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedDay, setExpandedDay] = useState(0);
  const [selectedActivities, setSelectedActivities] = useState({});
  const [currencyData, setCurrencyData] = useState(null);
  const [userCurrency, setUserCurrency] = useState('EUR');
  const [showAllCurrencies, setShowAllCurrencies] = useState(false);
  const [currencySearch, setCurrencySearch] = useState('');
  const [destPhotos, setDestPhotos] = useState(null);

  useEffect(() => {
    fetchCurrencyRates();
    fetchDestPhotos();
  }, []);

  const fetchCurrencyRates = async () => {
    try {
      const res = await fetch(`${API}/currency/rates`);
      const data = await res.json();
      setCurrencyData(data);
    } catch (e) { /* silent */ }
  };

  const fetchDestPhotos = async () => {
    try {
      const dest = (tripPlan?.destinations?.[0] || '').split(',')[0].trim();
      if (dest) {
        const res = await fetch(`${API}/destination/photos/${encodeURIComponent(dest)}`);
        const data = await res.json();
        setDestPhotos(data);
      }
    } catch (e) { /* silent */ }
  };

  const CURRENCY_NAMES = {
    EUR: 'Euro', USD: 'US Dollar', GBP: 'British Pound', JPY: 'Japanese Yen',
    CHF: 'Swiss Franc', AUD: 'Australian Dollar', CAD: 'Canadian Dollar',
    CNY: 'Chinese Yuan', HKD: 'Hong Kong Dollar', SGD: 'Singapore Dollar',
    SEK: 'Swedish Krona', NOK: 'Norwegian Krone', DKK: 'Danish Krone',
    NZD: 'New Zealand Dollar', ZAR: 'South African Rand', MXN: 'Mexican Peso',
    BRL: 'Brazilian Real', INR: 'Indian Rupee', KRW: 'South Korean Won',
    THB: 'Thai Baht', MYR: 'Malaysian Ringgit', IDR: 'Indonesian Rupiah',
    PHP: 'Philippine Peso', PLN: 'Polish Zloty', CZK: 'Czech Koruna',
    HUF: 'Hungarian Forint', TRY: 'Turkish Lira', ILS: 'Israeli Shekel',
    AED: 'UAE Dirham', SAR: 'Saudi Riyal', BGN: 'Bulgarian Lev',
    HRK: 'Croatian Kuna', RON: 'Romanian Leu', ISK: 'Icelandic Krona',
  };

  // Filter CURRENCY_NAMES to only show currencies available from the API
  const getAvailableCurrencies = () => {
    if (!currencyData?.rates) return Object.keys(CURRENCY_NAMES);
    return Object.keys(CURRENCY_NAMES).filter(c => c === 'EUR' || currencyData.rates[c]);
  };

  const formatPrice = (amount, currency = 'EUR') => {
    const symbols = {
      EUR: '\u20AC', USD: '$', GBP: '\u00A3', JPY: '\u00A5', CHF: 'CHF ',
      AUD: 'A$', CAD: 'C$', CNY: '\u00A5', HKD: 'HK$', SGD: 'S$',
      SEK: 'kr', NOK: 'kr', DKK: 'kr', NZD: 'NZ$', ZAR: 'R',
      MXN: 'MX$', BRL: 'R$', INR: '\u20B9', KRW: '\u20A9', THB: '\u0E3F',
      MYR: 'RM', PLN: 'z\u0142', CZK: 'K\u010D', TRY: '\u20BA', ILS: '\u20AA',
      AED: 'AED ', SAR: 'SAR ',
    };
    const sym = symbols[currency] || currency + ' ';
    return `${sym}${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  if (!tripPlan) return null;

  const destinations = tripPlan.destinations || [];
  const flights = tripPlan.selected_flights || [];
  const hotels = tripPlan.selected_hotels || [];
  const costBreakdown = tripPlan.cost_breakdown || {};
  const totalCost = tripPlan.total_estimated_cost || 0;
  const budgetStatus = tripPlan.budget_status || '';
  const days = tripPlan.duration_days || 5;
  const activities = tripPlan.activities || tripPlan.suggested_activities || [];
  const research = tripPlan.research_highlights || {};

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { id: 'flights', label: 'Flights', icon: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8' },
    { id: 'hotels', label: 'Hotels', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { id: 'activities', label: 'Activities', icon: 'M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'itinerary', label: 'Day Plan', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { id: 'costs', label: 'Costs', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  ];

  const toggleActivity = (dayIdx, actIdx) => {
    const key = `${dayIdx}-${actIdx}`;
    setSelectedActivities(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Trip save & share (#16, #17)
  const [saveStatus, setSaveStatus] = useState(null);
  const [shareCode, setShareCode] = useState(null);
  
  const saveTrip = async () => {
    try {
      const res = await fetch(`${API}/trip/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: 'guest',
          name: destinations.join(' to ') || 'My Trip',
          trip_plan: tripPlan,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSaveStatus('saved');
        setShareCode(data.share_code);
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (e) { setSaveStatus('error'); }
  };

  const copyShareLink = () => {
    if (shareCode) {
      const url = `${window.location.origin}?trip=${shareCode}`;
      navigator.clipboard.writeText(url).catch(() => {});
      setSaveStatus('copied');
      setTimeout(() => setSaveStatus(null), 2000);
    }
  };

  const generateItinerary = () => {
    const itinerary = [];
    const allActivities = [...activities];
    for (let d = 0; d < days; d++) {
      const dayActivities = [];
      const perDay = Math.ceil(allActivities.length / days);
      const startIdx = d * perDay;
      for (let i = startIdx; i < Math.min(startIdx + perDay, allActivities.length); i++) {
        dayActivities.push(allActivities[i]);
      }
      itinerary.push({
        day: d + 1,
        label: d === 0 ? 'Arrival Day' : d === days - 1 ? 'Departure Day' : `Day ${d + 1}`,
        activities: dayActivities,
      });
    }
    return itinerary;
  };

  const itinerary = generateItinerary();

  return (
    <div data-testid="trip-plan-card" style={styles.container}>
      {/* Header with destination photo */}
      <div style={{
        ...styles.header,
        ...(destPhotos?.hero ? {
          backgroundImage: `linear-gradient(180deg, rgba(10,18,30,0.3) 0%, rgba(10,18,30,0.85) 60%, rgba(10,18,30,0.98) 100%), url(${destPhotos.hero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '32px 24px 20px',
        } : {}),
      }}>
        <div style={styles.headerContent}>
          <div style={styles.headerLabel}>Your Trip Package</div>
          <h2 style={styles.headerTitle}>{destinations.join(' \u2192 ') || 'Custom Trip'}</h2>
          <div style={styles.headerMeta}>
            <span style={styles.metaBadge}>{days} Days</span>
            <span style={styles.metaBadge}>{tripPlan.num_travelers || 1} Traveler{(tripPlan.num_travelers || 1) > 1 ? 's' : ''}</span>
            <span style={{...styles.metaBadge, ...styles.priceBadge}}>{formatPrice(totalCost)}</span>
            {budgetStatus === 'over_budget' && (
              <span style={styles.warningBadge}>Over Budget</span>
            )}
          </div>
          {/* Save & Share (#16, #17) */}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button data-testid="save-trip-btn" onClick={saveTrip} style={{
              padding: '5px 14px', borderRadius: 6, border: '1px solid rgba(212,160,84,0.3)',
              background: saveStatus === 'saved' ? 'rgba(16,185,129,0.15)' : 'rgba(212,160,84,0.08)',
              color: saveStatus === 'saved' ? '#10b981' : '#d4a054', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}>
              {saveStatus === 'saved' ? '\u2713 Saved' : 'Save Trip'}
            </button>
            {shareCode && (
              <button data-testid="share-trip-btn" onClick={copyShareLink} style={{
                padding: '5px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.1)',
                background: saveStatus === 'copied' ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)',
                color: saveStatus === 'copied' ? '#3b82f6' : '#a0aec0', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>
                {saveStatus === 'copied' ? '\u2713 Copied' : 'Share Link'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabBar}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-testid={`trip-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {}),
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
            </svg>
            <span style={styles.tabLabel}>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={styles.tabContent}>
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div data-testid="trip-overview-tab">
            <div style={styles.overviewGrid}>
              {/* Quick Stats */}
              <div style={styles.statsRow}>
                <div style={styles.statCard}>
                  <div style={styles.statValue}>{flights.length || '-'}</div>
                  <div style={styles.statLabel}>Flight Options</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statValue}>{hotels.length || '-'}</div>
                  <div style={styles.statLabel}>Hotel Options</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statValue}>{activities.length || '-'}</div>
                  <div style={styles.statLabel}>Activities</div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statValue, color: '#d4a054'}}>{formatPrice(totalCost)}</div>
                  <div style={styles.statLabel}>Est. Total</div>
                </div>
              </div>

              {/* Budget Allocation */}
              {costBreakdown && Object.keys(costBreakdown).length > 0 && (
                <div style={styles.budgetSection}>
                  <h4 style={styles.sectionTitle}>Budget Allocation</h4>
                  <div style={styles.budgetBars}>
                    {Object.entries(costBreakdown).map(([key, val]) => {
                      const pct = totalCost > 0 ? (val / totalCost * 100) : 0;
                      const colors = {
                        flights: '#3b82f6', accommodation: '#10b981', hotels: '#10b981',
                        activities: '#f59e0b', daily_extras: '#f59e0b', food: '#ec4899',
                      };
                      return (
                        <div key={key} style={styles.budgetBarRow}>
                          <div style={styles.budgetBarLabel}>
                            <span>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                            <span style={styles.budgetBarValue}>{formatPrice(val)} ({pct.toFixed(0)}%)</span>
                          </div>
                          <div style={styles.budgetBarTrack}>
                            <div style={{
                              ...styles.budgetBarFill,
                              width: `${Math.min(pct, 100)}%`,
                              background: colors[key] || '#64748b',
                            }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div style={styles.actionRow}>
                <button
                  data-testid="modify-flights-btn"
                  style={styles.actionBtn}
                  onClick={() => sendMessage && sendMessage("I'd like to see different flight options for this trip")}
                >
                  Change Flights
                </button>
                <button
                  data-testid="modify-hotels-btn"
                  style={{...styles.actionBtn, ...styles.actionBtnAlt}}
                  onClick={() => sendMessage && sendMessage("Can you show me different hotel options?")}
                >
                  Change Hotels
                </button>
                <button
                  data-testid="modify-dates-btn"
                  style={{...styles.actionBtn, ...styles.actionBtnAlt}}
                  onClick={() => sendMessage && sendMessage("I'd like to adjust the travel dates")}
                >
                  Change Dates
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Flights Tab */}
        {activeTab === 'flights' && (
          <div data-testid="trip-flights-tab">
            {flights.length === 0 ? (
              <div style={styles.emptyState}>No flight data available. Ask Lauren for flight options.</div>
            ) : (
              <div style={styles.cardList}>
                {flights.map((flight, idx) => (
                  <div key={idx} data-testid={`flight-card-${idx}`} style={{
                    ...styles.flightCard,
                    ...(flight.recommended ? styles.recommendedCard : {}),
                  }}>
                    {flight.recommended && <div style={styles.recommendedTag}>Best Value</div>}
                    <div style={styles.flightHeader}>
                      <div style={styles.airlineName}>{flight.airline || 'Airline'}</div>
                      <div style={styles.flightPrice}>{formatPrice(flight.price, flight.currency || 'USD')}</div>
                    </div>
                    <div style={styles.flightRoute}>
                      <div style={styles.routePoint}>
                        <div style={styles.routeTime}>{flight.departure_time || 'TBD'}</div>
                        <div style={styles.routeCode}>{flight.origin || ''}</div>
                      </div>
                      <div style={styles.routeLine}>
                        <div style={styles.routeDuration}>{flight.duration || ''}</div>
                        <div style={styles.routeDash} />
                        <div style={styles.routeStops}>{flight.stops === 0 ? 'Direct' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}</div>
                      </div>
                      <div style={styles.routePoint}>
                        <div style={styles.routeTime}>{flight.arrival_time || 'TBD'}</div>
                        <div style={styles.routeCode}>{flight.destination || ''}</div>
                      </div>
                    </div>
                    <button
                      style={styles.selectBtn}
                      onClick={() => sendMessage && sendMessage(`I'll take flight option ${idx + 1} with ${flight.airline}`)}
                    >
                      Select This Flight
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Hotels Tab */}
        {activeTab === 'hotels' && (
          <div data-testid="trip-hotels-tab">
            {hotels.length === 0 ? (
              <div style={styles.emptyState}>No hotel data available. Ask Lauren for accommodation options.</div>
            ) : (
              <div style={styles.cardList}>
                {hotels.map((hotel, idx) => (
                  <div key={idx} data-testid={`hotel-card-${idx}`} style={{
                    ...styles.hotelCard,
                    ...(hotel.recommended ? styles.recommendedCard : {}),
                  }}>
                    {hotel.recommended && <div style={styles.recommendedTag}>Best Value</div>}
                    {destPhotos?.hotel_photo && (
                      <div style={{
                        height: '120px',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        backgroundImage: `url(${destPhotos.hotel_photo})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }} />
                    )}
                    <div style={styles.hotelHeader}>
                      <div>
                        <div style={styles.hotelName}>{hotel.hotel_name || hotel.name || 'Hotel'}</div>
                        <div style={styles.hotelRating}>
                          {Array.from({length: Math.round(hotel.rating || 0)}).map((_, i) => (
                            <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill="#d4a054" stroke="none">
                              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                            </svg>
                          ))}
                          <span style={styles.ratingText}>{hotel.rating}</span>
                        </div>
                      </div>
                      <div style={styles.hotelPrice}>
                        <div style={styles.hotelPriceAmount}>{formatPrice(hotel.price_per_night, hotel.currency || 'USD')}</div>
                        <div style={styles.hotelPriceLabel}>per night</div>
                      </div>
                    </div>
                    {hotel.room_type && <div style={styles.hotelRoom}>Room: {hotel.room_type}</div>}
                    <button
                      style={styles.selectBtn}
                      onClick={() => sendMessage && sendMessage(`I'll book the ${hotel.hotel_name || hotel.name} hotel`)}
                    >
                      Select This Hotel
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Activities Tab */}
        {activeTab === 'activities' && (
          <div data-testid="trip-activities-tab">
            {activities.length === 0 ? (
              <div style={styles.emptyState}>No activities found yet. Ask Lauren for activity suggestions.</div>
            ) : (
              <div style={styles.activityList}>
                <div style={styles.activityNote}>
                  <svg width="16" height="16" fill="none" stroke="#d4a054" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Activities are booked on-demand. You only pay when you confirm a booking.</span>
                </div>
                {activities.map((act, idx) => (
                  <div key={idx} data-testid={`activity-card-${idx}`} style={styles.activityCard}>
                    <div style={styles.activityInfo}>
                      <div style={styles.activityName}>{act.name || act.title || 'Activity'}</div>
                      <div style={styles.activityType}>{act.type || act.category || 'Experience'}</div>
                      {act.duration && <div style={styles.activityDuration}>{act.duration}</div>}
                    </div>
                    <div style={styles.activityActions}>
                      {act.price && <div style={styles.activityPrice}>{formatPrice(act.price)}</div>}
                      <button
                        style={styles.bookLaterBtn}
                        onClick={() => sendMessage && sendMessage(`I'd like to add "${act.name}" to my trip plan`)}
                      >
                        Add to Plan
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Itinerary / Day Plan Tab */}
        {activeTab === 'itinerary' && (
          <div data-testid="trip-itinerary-tab">
            <div style={styles.itineraryNote}>
              <svg width="16" height="16" fill="none" stroke="#d4a054" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Suggested daily plan. Click activities to add them to your booking. You'll only be charged when you confirm.</span>
            </div>
            {itinerary.map((day, dayIdx) => (
              <div key={dayIdx} data-testid={`itinerary-day-${dayIdx}`} style={styles.dayBlock}>
                <button
                  style={{
                    ...styles.dayHeader,
                    ...(expandedDay === dayIdx ? styles.dayHeaderActive : {}),
                  }}
                  onClick={() => setExpandedDay(expandedDay === dayIdx ? -1 : dayIdx)}
                >
                  <div style={styles.dayNumber}>Day {day.day}</div>
                  <div style={styles.dayLabel}>{day.label}</div>
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}
                    style={{ transform: expandedDay === dayIdx ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {expandedDay === dayIdx && (
                  <div style={styles.dayContent}>
                    {day.activities.length === 0 ? (
                      <div style={styles.dayEmpty}>
                        <p>Free day - explore on your own!</p>
                        <button
                          style={styles.suggestBtn}
                          onClick={() => sendMessage && sendMessage(`Suggest activities for day ${day.day} of my trip`)}
                        >
                          Get Suggestions
                        </button>
                      </div>
                    ) : (
                      day.activities.map((act, actIdx) => {
                        const isSelected = selectedActivities[`${dayIdx}-${actIdx}`];
                        return (
                          <div key={actIdx} style={{
                            ...styles.itineraryItem,
                            ...(isSelected ? styles.itineraryItemSelected : {}),
                          }}>
                            <div style={styles.timeSlot}>
                              <div style={styles.timeLabel}>
                                {actIdx === 0 ? 'Morning' : actIdx === 1 ? 'Afternoon' : 'Evening'}
                              </div>
                            </div>
                            <div style={styles.itineraryInfo}>
                              <div style={styles.itineraryName}>{act.name || act.title || 'Activity'}</div>
                              <div style={styles.itineraryType}>{act.type || act.category || ''}</div>
                            </div>
                            <button
                              style={{
                                ...styles.itineraryToggle,
                                ...(isSelected ? styles.itineraryToggleActive : {}),
                              }}
                              onClick={() => toggleActivity(dayIdx, actIdx)}
                            >
                              {isSelected ? 'Added' : 'Add'}
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            ))}
            {Object.values(selectedActivities).some(v => v) && (
              <div style={styles.bookSelectedBar}>
                <span>{Object.values(selectedActivities).filter(v => v).length} activities selected</span>
                <button
                  style={styles.bookSelectedBtn}
                  onClick={() => sendMessage && sendMessage(`I'd like to book the selected activities for my trip`)}
                >
                  Book Selected Activities
                </button>
              </div>
            )}
            {/* Interactive Daily Planner */}
            <div style={{ marginTop: '20px' }}>
              <DailyItineraryPlanner
                tripDays={itinerary.length || 5}
                activities={itinerary.flatMap((day, dayIdx) =>
                  (day.activities || []).map((a, i) => ({
                    day: day.day || dayIdx + 1,
                    name: a.name || a.title || 'Activity',
                    type: a.type || 'sightseeing',
                    time: i === 0 ? '09:00' : i === 1 ? '14:00' : '19:00',
                    duration: a.duration || '2 hours',
                  }))
                )}
                destination={tripPlan?.destinations?.[0] || ''}
                onSave={(days) => {
                  if (sendMessage) sendMessage(`I've organized my daily itinerary for this trip.`);
                }}
              />
            </div>
          </div>
        )}

        {/* Costs Tab */}
        {activeTab === 'costs' && (
          <div data-testid="trip-costs-tab">
            <div style={styles.costSection}>
              <h4 style={styles.sectionTitle}>Service Costs (What You Pay Us)</h4>
              <div style={styles.costTable}>
                {Object.entries(costBreakdown).filter(([k]) => k !== 'food').map(([key, val]) => (
                  <div key={key} style={styles.costRow}>
                    <span style={styles.costLabel}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    <span style={styles.costValue}>{formatPrice(val)}</span>
                  </div>
                ))}
                <div style={{...styles.costRow, ...styles.costRowTotal}}>
                  <span style={styles.costLabel}>Total Service Cost</span>
                  <span style={styles.costValue}>{formatPrice(totalCost)}</span>
                </div>
              </div>
            </div>

            {costBreakdown.food && (
              <div style={styles.costSection}>
                <h4 style={styles.sectionTitle}>Personal Expenses (Advisory)</h4>
                <div style={styles.advisoryNote}>
                  These costs are estimates for your personal spending. They are not part of our service.
                </div>
                <div style={styles.costTable}>
                  <div style={styles.costRow}>
                    <span style={styles.costLabel}>Food & Dining (est.)</span>
                    <span style={styles.costValue}>{formatPrice(costBreakdown.food)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Currency Converter */}
            {currencyData && (
              <div style={styles.currencySection}>
                <h4 style={styles.sectionTitle}>View in Different Currency</h4>
                {/* Popular currencies row */}
                <div style={styles.currencySelector}>
                  {['EUR', 'USD', 'GBP', 'JPY', 'CHF', 'AUD', 'CAD', 'CNY', 'INR'].map(cur => (
                    <button
                      key={cur}
                      style={{
                        ...styles.currencyBtn,
                        ...(userCurrency === cur ? styles.currencyBtnActive : {}),
                      }}
                      onClick={() => setUserCurrency(cur)}
                    >
                      {cur}
                    </button>
                  ))}
                  <button
                    style={{
                      ...styles.currencyBtn,
                      ...(showAllCurrencies ? styles.currencyBtnActive : {}),
                    }}
                    onClick={() => setShowAllCurrencies(!showAllCurrencies)}
                  >
                    {showAllCurrencies ? 'Less' : `+${Object.keys(currencyData.rates || {}).length - 9} more`}
                  </button>
                </div>

                {/* Expanded currency grid with search */}
                {showAllCurrencies && (
                  <div style={styles.allCurrenciesPanel}>
                    <input
                      type="text"
                      placeholder="Search currency..."
                      value={currencySearch}
                      onChange={(e) => setCurrencySearch(e.target.value)}
                      style={styles.currencySearchInput}
                    />
                    <div style={styles.allCurrenciesGrid}>
                      {Object.keys(currencyData.rates || {})
                        .filter(cur => {
                          if (!currencySearch) return true;
                          const search = currencySearch.toUpperCase();
                          const name = (CURRENCY_NAMES[cur] || '').toUpperCase();
                          return cur.includes(search) || name.includes(search);
                        })
                        .sort()
                        .map(cur => (
                          <button
                            key={cur}
                            style={{
                              ...styles.allCurrencyItem,
                              ...(userCurrency === cur ? styles.allCurrencyItemActive : {}),
                            }}
                            onClick={() => { setUserCurrency(cur); setShowAllCurrencies(false); }}
                          >
                            <span style={styles.currencyCode}>{cur}</span>
                            <span style={styles.currencyName}>{CURRENCY_NAMES[cur] || cur}</span>
                            <span style={styles.currencyRate}>
                              {formatPrice(totalCost * (currencyData.rates[cur] || 1), cur)}
                            </span>
                          </button>
                        ))
                      }
                    </div>
                  </div>
                )}

                {userCurrency !== 'EUR' && currencyData.rates && (
                  <div style={styles.convertedPrice}>
                    {formatPrice(totalCost * (currencyData.rates[userCurrency] || 1), userCurrency)}
                    <span style={styles.rateNote}> (rate: 1 EUR = {(currencyData.rates[userCurrency] || 1).toFixed(4)} {userCurrency})</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    background: 'linear-gradient(145deg, rgba(15,25,40,0.95), rgba(10,18,30,0.98))',
    borderRadius: '16px',
    border: '1px solid rgba(212,160,84,0.15)',
    overflow: 'hidden',
    marginTop: '12px',
    maxWidth: '100%',
  },
  header: {
    background: 'linear-gradient(135deg, rgba(212,160,84,0.12), rgba(212,160,84,0.04))',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(212,160,84,0.1)',
  },
  headerContent: {},
  headerLabel: {
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '1.5px',
    color: '#d4a054',
    marginBottom: '6px',
    fontWeight: 600,
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#f0e6d3',
    margin: '0 0 10px 0',
  },
  headerMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  metaBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 500,
    background: 'rgba(255,255,255,0.06)',
    color: '#a0aec0',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  priceBadge: {
    background: 'rgba(212,160,84,0.12)',
    color: '#d4a054',
    border: '1px solid rgba(212,160,84,0.2)',
    fontWeight: 700,
  },
  warningBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 600,
    background: 'rgba(239,68,68,0.12)',
    color: '#ef4444',
    border: '1px solid rgba(239,68,68,0.2)',
  },
  tabBar: {
    display: 'flex',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    padding: '0 8px',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '12px 16px',
    border: 'none',
    background: 'none',
    color: '#64748b',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    borderBottom: '2px solid transparent',
    transition: 'color 0.2s, border-color 0.2s',
  },
  tabActive: {
    color: '#d4a054',
    borderBottomColor: '#d4a054',
  },
  tabLabel: {
    fontSize: '13px',
  },
  tabContent: {
    padding: '20px',
    minHeight: '200px',
  },
  overviewGrid: {},
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    marginBottom: '20px',
  },
  statCard: {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '12px',
    padding: '14px 12px',
    textAlign: 'center',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#f0e6d3',
  },
  statLabel: {
    fontSize: '11px',
    color: '#64748b',
    marginTop: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  budgetSection: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#e2d5c3',
    margin: '0 0 12px 0',
  },
  budgetBars: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  budgetBarRow: {},
  budgetBarLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: '#a0aec0',
    marginBottom: '4px',
  },
  budgetBarValue: {
    fontWeight: 600,
    color: '#d4a054',
  },
  budgetBarTrack: {
    height: '6px',
    borderRadius: '3px',
    background: 'rgba(255,255,255,0.06)',
    overflow: 'hidden',
  },
  budgetBarFill: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.5s ease',
  },
  actionRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  actionBtn: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(212,160,84,0.3)',
    background: 'rgba(212,160,84,0.1)',
    color: '#d4a054',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  actionBtnAlt: {
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.04)',
    color: '#a0aec0',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#64748b',
    fontSize: '14px',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  flightCard: {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid rgba(255,255,255,0.06)',
    position: 'relative',
  },
  recommendedCard: {
    border: '1px solid rgba(212,160,84,0.3)',
    boxShadow: '0 0 20px rgba(212,160,84,0.08)',
  },
  recommendedTag: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    padding: '3px 10px',
    borderRadius: '12px',
    background: 'rgba(212,160,84,0.15)',
    color: '#d4a054',
    fontSize: '11px',
    fontWeight: 600,
  },
  flightHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '14px',
  },
  airlineName: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#f0e6d3',
  },
  flightPrice: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#d4a054',
  },
  flightRoute: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '14px',
  },
  routePoint: {
    textAlign: 'center',
  },
  routeTime: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#e2d5c3',
  },
  routeCode: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '2px',
  },
  routeLine: {
    flex: 1,
    textAlign: 'center',
    padding: '0 12px',
  },
  routeDuration: {
    fontSize: '12px',
    color: '#a0aec0',
    marginBottom: '4px',
  },
  routeDash: {
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(212,160,84,0.4), transparent)',
    margin: '4px 0',
  },
  routeStops: {
    fontSize: '11px',
    color: '#64748b',
  },
  selectBtn: {
    width: '100%',
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid rgba(212,160,84,0.3)',
    background: 'rgba(212,160,84,0.08)',
    color: '#d4a054',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  hotelCard: {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid rgba(255,255,255,0.06)',
    position: 'relative',
  },
  hotelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  hotelName: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#f0e6d3',
  },
  hotelRating: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    marginTop: '4px',
  },
  ratingText: {
    fontSize: '13px',
    color: '#d4a054',
    marginLeft: '4px',
    fontWeight: 600,
  },
  hotelPrice: {
    textAlign: 'right',
  },
  hotelPriceAmount: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#d4a054',
  },
  hotelPriceLabel: {
    fontSize: '11px',
    color: '#64748b',
  },
  hotelRoom: {
    fontSize: '13px',
    color: '#a0aec0',
    marginBottom: '14px',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  activityNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    borderRadius: '8px',
    background: 'rgba(212,160,84,0.08)',
    border: '1px solid rgba(212,160,84,0.15)',
    color: '#d4a054',
    fontSize: '12px',
    marginBottom: '8px',
  },
  activityCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    borderRadius: '10px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  activityInfo: {},
  activityName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#f0e6d3',
  },
  activityType: {
    fontSize: '12px',
    color: '#64748b',
    marginTop: '2px',
    textTransform: 'capitalize',
  },
  activityDuration: {
    fontSize: '11px',
    color: '#a0aec0',
    marginTop: '2px',
  },
  activityActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  activityPrice: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#d4a054',
  },
  bookLaterBtn: {
    padding: '6px 14px',
    borderRadius: '6px',
    border: '1px solid rgba(212,160,84,0.3)',
    background: 'rgba(212,160,84,0.08)',
    color: '#d4a054',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  itineraryNote: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 14px',
    borderRadius: '8px',
    background: 'rgba(212,160,84,0.08)',
    border: '1px solid rgba(212,160,84,0.15)',
    color: '#d4a054',
    fontSize: '12px',
    marginBottom: '12px',
  },
  dayBlock: {
    marginBottom: '8px',
    borderRadius: '10px',
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  dayHeader: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: '14px 16px',
    border: 'none',
    background: 'rgba(255,255,255,0.03)',
    color: '#e2d5c3',
    cursor: 'pointer',
    gap: '12px',
    transition: 'background 0.2s',
  },
  dayHeaderActive: {
    background: 'rgba(212,160,84,0.08)',
  },
  dayNumber: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#d4a054',
    minWidth: '50px',
  },
  dayLabel: {
    fontSize: '13px',
    color: '#a0aec0',
    flex: 1,
    textAlign: 'left',
  },
  dayContent: {
    padding: '12px 16px',
    background: 'rgba(0,0,0,0.15)',
  },
  dayEmpty: {
    textAlign: 'center',
    padding: '20px',
    color: '#64748b',
    fontSize: '13px',
  },
  suggestBtn: {
    marginTop: '8px',
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid rgba(212,160,84,0.3)',
    background: 'rgba(212,160,84,0.08)',
    color: '#d4a054',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  itineraryItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    borderRadius: '8px',
    marginBottom: '6px',
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.05)',
    transition: 'all 0.2s',
  },
  itineraryItemSelected: {
    background: 'rgba(212,160,84,0.08)',
    border: '1px solid rgba(212,160,84,0.2)',
  },
  timeSlot: {
    minWidth: '70px',
  },
  timeLabel: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    color: '#64748b',
    letterSpacing: '0.5px',
  },
  itineraryInfo: {
    flex: 1,
    marginLeft: '12px',
  },
  itineraryName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#f0e6d3',
  },
  itineraryType: {
    fontSize: '11px',
    color: '#64748b',
    textTransform: 'capitalize',
  },
  itineraryToggle: {
    padding: '5px 14px',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.04)',
    color: '#a0aec0',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  itineraryToggleActive: {
    border: '1px solid rgba(16,185,129,0.3)',
    background: 'rgba(16,185,129,0.12)',
    color: '#10b981',
  },
  bookSelectedBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    marginTop: '12px',
    borderRadius: '10px',
    background: 'rgba(212,160,84,0.1)',
    border: '1px solid rgba(212,160,84,0.2)',
    color: '#d4a054',
    fontSize: '13px',
    fontWeight: 500,
  },
  bookSelectedBtn: {
    padding: '8px 20px',
    borderRadius: '8px',
    border: 'none',
    background: '#d4a054',
    color: '#0f1928',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  costSection: {
    marginBottom: '20px',
  },
  costTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  costRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: 'rgba(15,25,40,0.8)',
  },
  costRowTotal: {
    background: 'rgba(212,160,84,0.08)',
    fontWeight: 700,
  },
  costLabel: {
    fontSize: '13px',
    color: '#a0aec0',
  },
  costValue: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#f0e6d3',
  },
  advisoryNote: {
    fontSize: '12px',
    color: '#64748b',
    fontStyle: 'italic',
    marginBottom: '10px',
  },
  currencySection: {
    marginTop: '16px',
    padding: '16px',
    borderRadius: '10px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  currencySelector: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '12px',
  },
  currencyBtn: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.04)',
    color: '#a0aec0',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  currencyBtnActive: {
    border: '1px solid rgba(212,160,84,0.3)',
    background: 'rgba(212,160,84,0.12)',
    color: '#d4a054',
  },
  convertedPrice: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#d4a054',
  },
  rateNote: {
    fontSize: '12px',
    fontWeight: 400,
    color: '#64748b',
  },
  allCurrenciesPanel: {
    marginTop: '10px',
    padding: '12px',
    borderRadius: '10px',
    background: 'rgba(0,0,0,0.2)',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  currencySearchInput: {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.04)',
    color: '#f0e6d3',
    fontSize: '13px',
    marginBottom: '10px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  allCurrenciesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '4px',
    maxHeight: '240px',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
  },
  allCurrencyItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 10px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.02)',
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'left',
  },
  allCurrencyItemActive: {
    border: '1px solid rgba(212,160,84,0.4)',
    background: 'rgba(212,160,84,0.1)',
  },
  currencyCode: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#d4a054',
  },
  currencyName: {
    fontSize: '10px',
    color: '#64748b',
    marginTop: '1px',
  },
  currencyRate: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#f0e6d3',
    marginTop: '3px',
  },
};

// Responsive override for mobile
if (typeof window !== 'undefined' && window.innerWidth < 640) {
  styles.statsRow.gridTemplateColumns = 'repeat(2, 1fr)';
  styles.tabBar.gap = '0';
  styles.tab.padding = '10px 10px';
  styles.tabLabel.fontSize = '11px';
  styles.header.padding = '16px';
  styles.tabContent.padding = '14px';
  styles.flightRoute.flexDirection = 'column';
  styles.flightRoute.gap = '8px';
}

export default TripPlanCard;
